<script>
import * as m from '@app/paraglide/messages.js';
import { onMount, onDestroy } from 'svelte';
import {
    modalState, hideModal
} from '@app/store/modalStore.svelte.js';
import { toast } from '@zerodevx/svelte-toast';
import {
    projectState
} from '@app/store/projectStore.svelte.js';
import {
    eventGroupState
} from '@app/store/eventGroupStore.svelte.js';
import {
    markerState
} from '@app/store/markerStore.svelte.js';
import {
    insertHistoryAction
} from '@app/store/historyStore.svelte.js';
import _Marker from "@cclib/classes/marker.js";
import _EventGroup from "@cclib/classes/eventGroup.js";
import _Event from "@cclib/classes/event.js";
import convertToHtml from "@cclib/functions/quill/convertToHtml.js";
import {
    fade
} from 'svelte/transition';
import Swal from 'sweetalert2';
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import { v4 as uuidv4 } from "uuid";

import tcLib from "@cclib/lib/timecode.js";

const ffmpeg = window.ffmpeg;

const hexColors = [
    "#ff66cc", "#00cc00", "#0066ff", "#6600ff", "#ff0066",
    "#6666ff", "#00cc99", "#99ccff", "#ff9966", "#ff33cc"
];

const MIN_CONFIDENCE = 60; // Minimum confidence threshold for OCR results
const MIN_TEXT_LENGTH = 3;  // Minimum character length to filter OCR noise
const SIMILARITY_THRESHOLD = 0.8; // Minimum similarity (0-1) for fuzzy text deduplication

const supportedLanguages = [
    { code: "eng", name: "English" },
    { code: "fra", name: "French" },
    { code: "deu", name: "German" },
    { code: "spa", name: "Spanish" },
    { code: "por", name: "Portuguese" },
    { code: "ita", name: "Italian" },
    { code: "nld", name: "Dutch" },
    { code: "rus", name: "Russian" },
    { code: "jpn", name: "Japanese" },
    { code: "kor", name: "Korean" },
    { code: "chi_sim", name: "Chinese (Simplified)" },
    { code: "chi_tra", name: "Chinese (Traditional)" },
    { code: "ara", name: "Arabic" },
    { code: "hin", name: "Hindi" },
    { code: "tur", name: "Turkish" },
    { code: "pol", name: "Polish" },
    { code: "vie", name: "Vietnamese" },
    { code: "tha", name: "Thai" }
];

const samplingRates = [
    { value: 0.25, label: () => m.ocr_sampling_high_detail() },
    { value: 0.5, label: () => m.ocr_sampling_recommended() },
    { value: 1, label: () => m.ocr_sampling_balanced() },
    { value: 2, label: () => m.ocr_sampling_fast() }
];

const cropRegions = [
    { value: "full", label: () => m.ocr_crop_full() },
    { value: "lower_third", label: () => m.ocr_crop_lower_third() },
    { value: "lower_half", label: () => m.ocr_crop_lower_half() },
    { value: "upper_third", label: () => m.ocr_crop_upper_third() },
    { value: "center_strip", label: () => m.ocr_crop_center_strip() },
    { value: "custom", label: () => m.ocr_crop_custom() }
];

const psmModes = [
    { value: "3", label: () => m.ocr_psm_auto() },
    { value: "6", label: () => m.ocr_psm_block() },
    { value: "11", label: () => m.ocr_psm_sparse() },
    { value: "7", label: () => m.ocr_psm_single_line() }
];

let selectedLanguage = $state("eng");
let selectedSamplingRate = $state(0.5);
let selectedCropRegion = $state("lower_third");
let selectedPsm = $state("6");
let imagePreprocessing = $state(true);

let dualPass = $state(true);  // Run OCR on both normal and inverted images

// Custom crop values (percentage 0-100)
let customCropTop = $state(66);
let customCropBottom = $state(100);
let customCropLeft = $state(0);
let customCropRight = $state(100);
let progress = $state(0);
let statusMsg = $state("");
let processing = $state(false);
let phase = $state("idle"); // idle, extracting, ocr, complete
let cancelled = $state(false);
let ffmpegCommand = null;
let tesseractWorker = null;
let ocrResults = $state([]);
let tempDir = "";

let estimatedFrames = $derived(
    projectState.media?.info?.duration 
        ? Math.ceil(projectState.media.info.duration / selectedSamplingRate) 
        : 0
);

async function startOcr() {
    if (!projectState.media.localPath) {
        toast.push(m.ocr_failed({ error: m.ocr_no_media_error() }), { classes: ['toast-warning'] });
        return;
    }

    processing = true;
    cancelled = false;
    ocrResults = [];
    progress = 0;
    phase = "extracting";
    statusMsg = m.ocr_extracting_frames({ progress: "0" });
    toast.push(m.ocr_started_toast(), { classes: ['toast-info'] });

    try {
        // Create temp directory for frames
        const tempPrefix = window.path.join(window.os.tmpdir(), 'cccocr-');
        tempDir = await window.fs.mkdtemp(tempPrefix);

        // Phase 1: Extract frames with FFmpeg (normal pass)
        await extractFrames("frame");
        
        if (cancelled) {
            await cleanup();
            return;
        }

        // Phase 1b: Extract inverted frames if dual-pass enabled
        if (dualPass) {
            statusMsg = m.ocr_extracting_inverted({ progress: "0" });
            await extractFrames("inv", true);

            if (cancelled) {
                await cleanup();
                return;
            }
        }

        // Phase 2: Run OCR on extracted frames
        phase = "ocr";
        await processOcr();

        if (cancelled) {
            await cleanup();
            return;
        }

        // Phase 3: Show results
        phase = "complete";
        progress = 100;
        statusMsg = m.ocr_complete();
        toast.push(m.ocr_complete_toast(), { classes: ['toast-success'] });

        if (ocrResults.length === 0) {
            await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.ok,
                title: m.ocr_complete(),
                text: m.ocr_no_text_found(),
                icon: 'info',
                confirmButtonText: m.ocr_close_button(),
            });
            await cleanup();
            hideModal();
            return;
        }

        // Ask user how to import
        let importResult = await Swal.fire({
            ...SWAL_BASE,
            customClass: {
                ...SWAL_CLASSES.trial,
                cancelButton: SWAL_CLASSES.confirmCancel.cancelButton,
            },
            title: m.ocr_complete(),
            text: m.ocr_complete_text({ count: ocrResults.length }),
            icon: 'success',
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonText: m.ocr_import_as_events(),
            denyButtonText: m.ocr_import_as_markers(),
            cancelButtonText: m.ocr_cancel_button(),
        });

        if (importResult.isConfirmed) {
            importAsEventGroup();
        } else if (importResult.isDenied) {
            importAsMarkers();
        }

        await cleanup();
        hideModal();

    } catch (err) {
        console.error("OCR Error:", err);
        toast.push(m.ocr_failed({ error: err.message }), { classes: ['toast-warning'] });
        await cleanup();
        hideModal();
    }
}

function buildVideoFilterChain() {
    let filters = [`fps=1/${selectedSamplingRate}`];

    // Apply crop filter based on selected region
    if (selectedCropRegion !== "full") {
        let cropFilter;
        switch (selectedCropRegion) {
            case "lower_third":
                cropFilter = "crop=iw:ih/3:0:ih*2/3";
                break;
            case "lower_half":
                cropFilter = "crop=iw:ih/2:0:ih/2";
                break;
            case "upper_third":
                cropFilter = "crop=iw:ih/3:0:0";
                break;
            case "center_strip":
                cropFilter = "crop=iw:ih/3:0:ih/3";
                break;
            case "custom": {
                let t = customCropTop / 100;
                let b = customCropBottom / 100;
                let l = customCropLeft / 100;
                let r = customCropRight / 100;
                let h = b - t;
                let w = r - l;
                if (h > 0 && w > 0) {
                    cropFilter = `crop=iw*${w.toFixed(4)}:ih*${h.toFixed(4)}:iw*${l.toFixed(4)}:ih*${t.toFixed(4)}`;
                }
                break;
            }
        }
        if (cropFilter) filters.push(cropFilter);
    }

    // Apply image preprocessing for better OCR accuracy
    if (imagePreprocessing) {
        filters.push(
            "format=gray",
            "scale=iw*2:ih*2",
            "eq=contrast=1.5:brightness=0.05",
            "unsharp=5:5:1.0"
        );
    }

    return filters.join(',');
}

function extractFrames(prefix = "frame", invert = false) {
    return new Promise((resolve, reject) => {
        const outputPattern = window.path.join(tempDir, `${prefix}_%06d.jpg`);
        const duration = projectState.media.info.duration || 10;
        let vfChain = buildVideoFilterChain();

        // Append negate filter for inverted pass (before any grayscale conversion)
        if (invert) {
            vfChain += ',negate';
        }

        ffmpegCommand = ffmpeg(projectState.media.localPath)
            .outputOptions([
                `-vf`, vfChain,
                `-q:v`, `2`
            ])
            .output(outputPattern)
            .on('start', (commandLine) => {
                console.log('OCR FFmpeg command:', commandLine);
            })
            .on('progress', (prog) => {
                if (cancelled) {
                    ffmpegCommand.kill('SIGKILL');
                    return;
                }
                let tcSec = tcLib.tcMsToSec(prog.timemark);
                let pct = Math.min(((tcSec / duration) * 100), 100).toFixed(1);
                progress = parseFloat(pct);
                statusMsg = m.ocr_extracting_frames({ progress: pct });
            })
            .on('end', () => {
                ffmpegCommand = null;
                if (!cancelled) {
                    progress = 100;
                    resolve();
                } else {
                    resolve();
                }
            })
            .on('error', (err) => {
                ffmpegCommand = null;
                if (cancelled) {
                    resolve();
                } else {
                    reject(err);
                }
            });

        ffmpegCommand.run();
    });
}

async function processOcr() {
    const { createWorker } = await import('tesseract.js');
    
    statusMsg = m.ocr_initializing();
    tesseractWorker = await createWorker(selectedLanguage);
    await tesseractWorker.setParameters({ tessedit_pageseg_mode: selectedPsm });

    // Get list of normal frame files
    const files = await window.fs.readdir(tempDir);
    const frameFiles = files
        .filter(f => f.startsWith('frame_') && f.endsWith('.jpg'))
        .sort();

    // Get inverted frame files if dual-pass is enabled
    const invFrameFiles = dualPass
        ? files.filter(f => f.startsWith('inv_') && f.endsWith('.jpg')).sort()
        : [];

    const totalFrames = frameFiles.length;
    if (totalFrames === 0) {
        if (tesseractWorker) {
            await tesseractWorker.terminate();
            tesseractWorker = null;
        }
        return;
    }

    let rawResults = [];

    for (let i = 0; i < totalFrames; i++) {
        if (cancelled) break;

        const framePath = window.path.join(tempDir, frameFiles[i]);
        const frameNumber = i + 1;
        const timestamp = i * selectedSamplingRate;

        progress = parseFloat(((frameNumber / totalFrames) * 100).toFixed(1));
        statusMsg = m.ocr_processing_status({ current: frameNumber, total: totalFrames });

        try {
            // Normal pass
            const { data } = await tesseractWorker.recognize(framePath);
            let bestData = data;

            // Inverted pass — compare and keep higher confidence result
            if (dualPass && invFrameFiles[i]) {
                const invPath = window.path.join(tempDir, invFrameFiles[i]);
                const { data: invData } = await tesseractWorker.recognize(invPath);
                const invText = invData.text ? invData.text.trim() : "";

                if (invText.length >= MIN_TEXT_LENGTH && invData.confidence > bestData.confidence) {
                    bestData = invData;
                }
            }

            const text = bestData.text ? bestData.text.trim() : "";
            
            if (text.length >= MIN_TEXT_LENGTH && bestData.confidence > MIN_CONFIDENCE) {
                // Compute position from word bounding boxes
                let position = computePosition(bestData);
                rawResults.push({
                    timestamp,
                    text,
                    confidence: bestData.confidence,
                    xPos: position.xPos,
                    yPos: position.yPos
                });
            }
        } catch (err) {
            console.warn(`OCR failed for frame ${frameNumber}:`, err.message);
        }
    }

    if (tesseractWorker) {
        await tesseractWorker.terminate();
        tesseractWorker = null;
    }

    // Aggregate consecutive frames with same text
    ocrResults = aggregateResults(rawResults);
}

function computePosition(data) {
    // Use word-level bounding boxes to determine position
    let xPos = "center";
    let yPos = "end";

    if (data.words && data.words.length > 0) {
        // Calculate aggregate bounding box from all words
        let minX = Infinity, minY = Infinity, maxX = 0, maxY = 0;
        for (const word of data.words) {
            const bbox = word.bbox;
            if (bbox.x0 < minX) minX = bbox.x0;
            if (bbox.y0 < minY) minY = bbox.y0;
            if (bbox.x1 > maxX) maxX = bbox.x1;
            if (bbox.y1 > maxY) maxY = bbox.y1;
        }

        // Get image dimensions from the first word's page-level data
        // Tesseract words have a bbox with absolute pixel coordinates
        // We'll use the page dimensions if available, otherwise estimate
        let pageWidth = data.width || maxX + 50;
        let pageHeight = data.height || maxY + 50;

        // Calculate center point of the text block
        let centerX = (minX + maxX) / 2;
        let centerY = (minY + maxY) / 2;

        // Horizontal position (split into thirds)
        let xRatio = centerX / pageWidth;
        if (xRatio < 0.33) {
            xPos = "start";
        } else if (xRatio > 0.66) {
            xPos = "end";
        } else {
            xPos = "center";
        }

        // Vertical position (split into thirds)
        let yRatio = centerY / pageHeight;
        if (yRatio < 0.33) {
            yPos = "start";
        } else if (yRatio > 0.66) {
            yPos = "end";
        } else {
            yPos = "center";
        }
    }

    return { xPos, yPos };
}

function aggregateResults(rawResults) {
    if (rawResults.length === 0) return [];

    let aggregated = [];
    let current = {
        startTime: rawResults[0].timestamp,
        endTime: rawResults[0].timestamp + selectedSamplingRate,
        text: rawResults[0].text,
        confidence: rawResults[0].confidence,
        xPos: rawResults[0].xPos,
        yPos: rawResults[0].yPos,
        count: 1
    };

    for (let i = 1; i < rawResults.length; i++) {
        const result = rawResults[i];
        const normalizedCurrent = normalizeText(current.text);
        const normalizedNew = normalizeText(result.text);

        // If similar text as current group and consecutive timestamp, extend
        const similarity = textSimilarity(normalizedCurrent, normalizedNew);
        if (similarity >= SIMILARITY_THRESHOLD && 
            (result.timestamp - current.endTime) <= selectedSamplingRate * 1.5) {
            current.endTime = result.timestamp + selectedSamplingRate;
            current.confidence = ((current.confidence * current.count) + result.confidence) / (current.count + 1);
            // Keep the higher-confidence text when texts differ
            if (similarity < 1 && result.confidence > current.confidence) {
                current.text = result.text;
            }
            current.count++;
        } else {
            // Push current group and start new one
            aggregated.push({
                startTime: current.startTime,
                endTime: current.endTime,
                text: current.text,
                confidence: current.confidence,
                xPos: current.xPos,
                yPos: current.yPos
            });
            current = {
                startTime: result.timestamp,
                endTime: result.timestamp + selectedSamplingRate,
                text: result.text,
                confidence: result.confidence,
                xPos: result.xPos,
                yPos: result.yPos,
                count: 1
            };
        }
    }

    // Push the last group
    aggregated.push({
        startTime: current.startTime,
        endTime: current.endTime,
        text: current.text,
        confidence: current.confidence,
        xPos: current.xPos,
        yPos: current.yPos
    });

    return aggregated;
}

function normalizeText(text) {
    return text.replace(/\s+/g, ' ').trim().toLowerCase();
}

function levenshteinDistance(a, b) {
    const m = a.length, n = b.length;
    if (m === 0) return n;
    if (n === 0) return m;

    let prev = Array.from({ length: n + 1 }, (_, i) => i);
    let curr = new Array(n + 1);

    for (let i = 1; i <= m; i++) {
        curr[0] = i;
        for (let j = 1; j <= n; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            curr[j] = Math.min(
                prev[j] + 1,      // deletion
                curr[j - 1] + 1,  // insertion
                prev[j - 1] + cost // substitution
            );
        }
        [prev, curr] = [curr, prev];
    }
    return prev[n];
}

function textSimilarity(a, b) {
    const maxLen = Math.max(a.length, b.length);
    if (maxLen === 0) return 1;
    return 1 - levenshteinDistance(a, b) / maxLen;
}

function importAsMarkers() {
    const newList = {
        id: uuidv4(),
        name: m.ocr_results_name(),
        color: hexColors[Math.floor(Math.random() * hexColors.length)],
        markers: ocrResults.map(result => ({
            ...(new _Marker({
                time: result.startTime,
                endTime: result.endTime,
                comment: result.text
            }))
        }))
    };

    markerState.lists = [...markerState.lists, newList];
    markerState.selected = markerState.lists.length - 1;

    insertHistoryAction({
        name: "OCR Import Markers",
        eventGroup: false,
        snapshots: [{
            store: "markerState",
            value: JSON.stringify(markerState)
        }]
    });

    toast.push(m.ocr_markers_imported_toast(), { classes: ['toast-success'] });
}

function importAsEventGroup() {
    let eventGroupDefaults = JSON.parse(localStorage.getItem("cc-event-group-defaults")) || {};
    let evg = { ...(new _EventGroup()) };
    evg.name = "Subtitles";
    evg.maxCps = eventGroupDefaults.maxCps || 9999;
    evg.maxWpm = eventGroupDefaults.maxWpm || 9999;
    evg.maxChars = eventGroupDefaults.maxChars || 9999;
    evg.maxLines = eventGroupDefaults.maxLines || 9999;
    evg.maxDuration = eventGroupDefaults.maxDuration || 9999;
    evg.minDuration = eventGroupDefaults.minDuration || 0.1;
    evg.overlap = eventGroupDefaults.overlap;
    evg.illegalChars = eventGroupDefaults.illegalChars;
    evg.fontFamily = eventGroupDefaults.fontFamily ?? "plexMono";
    evg.fontSize = eventGroupDefaults.fontSize || 16;

    evg.events = ocrResults.map(result => ({
        ...(new _Event({
            start: result.startTime,
            end: result.endTime,
            text: convertToHtml(result.text),
            xPos: result.xPos,
            yPos: result.yPos
        }))
    }));

    eventGroupState.push(evg);
    projectState.selected = eventGroupState.length - 1;

    insertHistoryAction({
        name: "OCR Import Event Group",
        eventGroup: projectState.selected,
        snapshots: [{
            store: "eventGroupState",
            value: JSON.stringify(eventGroupState)
        }]
    });

    toast.push(m.ocr_events_imported_toast(), { classes: ['toast-success'] });
}

async function cancelOcr() {
    cancelled = true;

    if (ffmpegCommand) {
        try {
            ffmpegCommand.kill('SIGKILL');
        } catch (e) {
            console.warn("Error killing FFmpeg:", e);
        }
        ffmpegCommand = null;
    }

    if (tesseractWorker) {
        try {
            await tesseractWorker.terminate();
        } catch (e) {
            console.warn("Error terminating Tesseract worker:", e);
        }
        tesseractWorker = null;
    }

    toast.push(m.ocr_cancelled(), { classes: ['toast-info'] });
    await cleanup();
    processing = false;
    phase = "idle";
    progress = 0;
    statusMsg = "";
}

async function cleanup() {
    if (tempDir) {
        try {
            const files = await window.fs.readdir(tempDir);
            for (const file of files) {
                await window.fs.unlink(window.path.join(tempDir, file));
            }
            await window.fs.rmdir(tempDir);
        } catch (e) {
            console.warn("Error cleaning up temp files:", e);
        }
        tempDir = "";
    }
}

function closeModal() {
    if (processing) {
        cancelOcr();
    }
    hideModal();
}

onDestroy(() => {
    if (processing) {
        cancelled = true;
        if (ffmpegCommand) {
            try { ffmpegCommand.kill('SIGKILL'); } catch(e) {}
            ffmpegCommand = null;
        }
        if (tesseractWorker) {
            tesseractWorker.terminate().catch(() => {});
            tesseractWorker = null;
        }
    }
});
</script>

<div transition:fade={{ duration: 100 }} class="modal {modalState.selected === 'ocrMedia' ? 'show d-block' : ''}" role="dialog" aria-labelledby="ocrMediaTitle" tabindex="-1" id="OcrMediaModal">
    <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="ocrMediaTitle">{m.ocr_media_title()}</h4>
                {#if !processing}
                    <button type="button" class="btn-close" onclick={closeModal} aria-label={m.ocr_close_button()}></button>
                {/if}
            </div>
            <div class="modal-body">
                <p class="text-muted small">{m.ocr_description()}</p>

                {#if phase === "idle"}
                    <!-- Configuration UI -->
                    <div class="mb-3">
                        <label for="ocrLanguage" class="form-label">{m.ocr_language_label()}</label>
                        <select id="ocrLanguage" class="form-select form-select-sm" bind:value={selectedLanguage}>
                            {#each supportedLanguages as lang}
                                <option value={lang.code}>{lang.name}</option>
                            {/each}
                        </select>
                    </div>

                    <div class="mb-3">
                        <label for="ocrSamplingRate" class="form-label">{m.ocr_sampling_rate_label()}</label>
                        <select id="ocrSamplingRate" class="form-select form-select-sm" bind:value={selectedSamplingRate}>
                            {#each samplingRates as rate}
                                <option value={rate.value}>{rate.label()}</option>
                            {/each}
                        </select>
                    </div>

                    <div class="mb-3">
                        <label for="ocrCropRegion" class="form-label">{m.ocr_crop_region_label()}</label>
                        <select id="ocrCropRegion" class="form-select form-select-sm" bind:value={selectedCropRegion}>
                            {#each cropRegions as region}
                                <option value={region.value}>{region.label()}</option>
                            {/each}
                        </select>
                    </div>

                    {#if selectedCropRegion === "custom"}
                        <div class="row g-2 mb-3">
                            <div class="col-6">
                                <label for="cropTop" class="form-label small">{m.ocr_crop_top()}</label>
                                <input id="cropTop" type="number" class="form-control form-control-sm" bind:value={customCropTop} min="0" max="100" />
                            </div>
                            <div class="col-6">
                                <label for="cropBottom" class="form-label small">{m.ocr_crop_bottom()}</label>
                                <input id="cropBottom" type="number" class="form-control form-control-sm" bind:value={customCropBottom} min="0" max="100" />
                            </div>
                            <div class="col-6">
                                <label for="cropLeft" class="form-label small">{m.ocr_crop_left()}</label>
                                <input id="cropLeft" type="number" class="form-control form-control-sm" bind:value={customCropLeft} min="0" max="100" />
                            </div>
                            <div class="col-6">
                                <label for="cropRight" class="form-label small">{m.ocr_crop_right()}</label>
                                <input id="cropRight" type="number" class="form-control form-control-sm" bind:value={customCropRight} min="0" max="100" />
                            </div>
                        </div>
                    {/if}

                    <div class="mb-3">
                        <label for="ocrPsm" class="form-label">{m.ocr_psm_label()}</label>
                        <select id="ocrPsm" class="form-select form-select-sm" bind:value={selectedPsm}>
                            {#each psmModes as mode}
                                <option value={mode.value}>{mode.label()}</option>
                            {/each}
                        </select>
                    </div>

                    <div class="form-check mb-3">
                        <input class="form-check-input" type="checkbox" id="ocrPreprocessing" bind:checked={imagePreprocessing} />
                        <label class="form-check-label" for="ocrPreprocessing">{m.ocr_preprocessing_label()}</label>
                        <div class="form-text">{m.ocr_preprocessing_description()}</div>
                    </div>

                    <div class="form-check mb-3">
                        <input class="form-check-input" type="checkbox" id="ocrDualPass" bind:checked={dualPass} />
                        <label class="form-check-label" for="ocrDualPass">{m.ocr_dual_pass_label()}</label>
                        <div class="form-text">{m.ocr_dual_pass_description()}</div>
                    </div>

                    {#if estimatedFrames > 0}
                        <p class="text-muted small">
                            <i class="bi bi-info-circle" aria-hidden="true"></i>
                            {m.ocr_frame_count_warning({ count: estimatedFrames })}
                        </p>
                    {/if}
                {:else}
                    <!-- Progress UI -->
                    <div class="progress mb-2" role="progressbar" aria-label={m.ocr_progress_aria_label()} aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100">
                        <div class="progress-bar bg-primary progress-bar-striped progress-bar-animated" style="width: {progress}%;">
                            {progress}%
                        </div>
                    </div>
                    <p class="text-center text-muted small" aria-live="polite">{statusMsg}</p>
                {/if}
            </div>
            <div class="modal-footer">
                {#if phase === "idle"}
                    <button type="button" class="btn btn-outline-secondary" onclick={closeModal}>{m.ocr_close_button()}</button>
                    <button type="button" class="btn btn-primary" onclick={startOcr} disabled={!projectState.media.localPath}>{m.ocr_start_button()}</button>
                {:else if processing}
                    <button type="button" class="btn btn-danger" onclick={cancelOcr}>{m.ocr_cancel_button()}</button>
                {/if}
            </div>
        </div>
    </div>
</div>
